import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp, HoverHintTrigger } from "@/components/ui/title-help";
import { RepAgencyDetailLink } from "@/components/inside-sales/RepAgencyDetailLink";
import {
  commissionsRulesQueryKey,
  getCommissionRules,
  listCommissionStatements,
  money,
  previewCommissionStatement,
  sendCommissionStatement,
} from "./commissionsApi";

type StatementRow = Awaited<ReturnType<typeof listCommissionStatements>>["rows"][number];

function statementStatusLabel(status: string, recipients: unknown): string {
  if (
    recipients != null &&
    typeof recipients === "object" &&
    !Array.isArray(recipients) &&
    (recipients as { sentVia?: string }).sentVia === "dry_run"
  ) {
    return `${status} (dry run)`;
  }
  return status;
}

function statementFreshdeskLabel(recipients: unknown, freshdeskTicketId: unknown): string {
  if (
    recipients != null &&
    typeof recipients === "object" &&
    !Array.isArray(recipients) &&
    (recipients as { sentVia?: string }).sentVia === "dry_run"
  ) {
    return "Dry run — no ticket";
  }
  return freshdeskTicketId != null ? String(freshdeskTicketId) : "—";
}

export function CommissionStatementsPage() {
  const queryClient = useQueryClient();
  const [runIdFilter, setRunIdFilter] = useState("");
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const runId = runIdFilter.trim() !== "" ? Number(runIdFilter) : undefined;
  const rulesQuery = useQuery({
    queryKey: commissionsRulesQueryKey,
    queryFn: getCommissionRules,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });
  const cutoverDryRun = rulesQuery.data?.cutoverDryRun ?? rulesQuery.data?.statementsDryRun ?? false;
  const pageHoverHint = cutoverDryRun
    ? "Cutover dry-run is on: send actions create statement rows and mark them sent without emailing via Freshdesk."
    : "Statements are created when you send from an approved run. Resend individual statements here.";
  const sendHoverHint = cutoverDryRun
    ? "Cutover dry-run: generates the statement record only — no Freshdesk email to the rep."
    : "Email this statement to contacts tagged commission statement via Freshdesk.";

  const listQuery = useQuery({
    queryKey: ["commissions", "statements", runId ?? "all"],
    queryFn: () => listCommissionStatements(Number.isFinite(runId) ? runId : undefined),
  });

  const sendMutation = useMutation({
    mutationFn: (id: number) => sendCommissionStatement(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["commissions", "statements"] });
    },
  });

  const columns: HubDataTableColumn<StatementRow>[] = useMemo(
    () => [
      {
        id: "run",
        header: "Run",
        overflow: "nowrap",
        sortValue: (row) => Number(row.run_id),
        cell: (row) => String(row.run_id),
      },
      {
        id: "rep",
        header: "Rep",
        sortValue: (row) => String(row.rep_agency_name ?? ""),
        cell: (row) => (
          <RepAgencyDetailLink
            repAgencyId={row.rep_agency_id != null ? Number(row.rep_agency_id) : null}
            repAgencyName={String(row.rep_agency_name ?? "")}
          />
        ),
      },
      {
        id: "kind",
        header: "Kind",
        overflow: "nowrap",
        sortValue: (row) => String(row.kind),
        cell: (row) => String(row.kind),
      },
      {
        id: "amount",
        header: "Amount",
        align: "right",
        overflow: "nowrap",
        sortValue: (row) => Number(row.total_amount),
        cell: (row) => money(row.total_amount as string | number),
      },
      {
        id: "status",
        header: "Status",
        sortValue: (row) => statementStatusLabel(String(row.status), row.recipients),
        cell: (row) => statementStatusLabel(String(row.status), row.recipients),
      },
      {
        id: "freshdesk",
        header: "Freshdesk",
        sortValue: (row) => statementFreshdeskLabel(row.recipients, row.freshdesk_ticket_id),
        cell: (row) => statementFreshdeskLabel(row.recipients, row.freshdesk_ticket_id),
      },
      {
        id: "actions",
        header: "Actions",
        overflow: "nowrap",
        sortValue: () => "",
        cell: (row) => {
          const id = Number(row.id);
          return (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={async () => {
                  const preview = await previewCommissionStatement(id);
                  setPreviewHtml(preview.html);
                }}
              >
                Preview
              </Button>
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    onClick={() => sendMutation.mutate(id)}
                    disabled={sendMutation.isPending}
                  >
                    {cutoverDryRun ? "Generate" : "Send"}
                  </Button>
                }
              >
                {sendHoverHint}
              </HoverHintTrigger>
            </div>
          );
        },
      },
    ],
    [cutoverDryRun, sendHoverHint, sendMutation],
  );

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint={pageHoverHint}>Commission statements</PageTitleWithHelp>

      <Card>
        <CardBody className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="run-id-filter">Filter by run id</Label>
            <Input
              id="run-id-filter"
              value={runIdFilter}
              onChange={(event) => setRunIdFilter(event.target.value)}
              placeholder="optional"
            />
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title="Archive"
        columns={columns}
        rows={listQuery.data?.rows ?? []}
        rowKey={(row) => Number(row.id)}
        loading={listQuery.isLoading}
        error={listQuery.isError ? "Could not load statements." : null}
        empty="No statements."
        isFiltered={runIdFilter.trim() !== ""}
        filteredEmpty="No statements match this run id."
      />

      {previewHtml != null ? (
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
          </CardHeader>
          <CardBody>
            <iframe title="Statement preview" className="h-[480px] w-full rounded border" srcDoc={previewHtml} />
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
